import type { KbAdminEditorSectionProps } from "./kbAdminEditorProps";

export function KbAdminEditorSplitSection(props: KbAdminEditorSectionProps) {
  const {
    aiFollowOnDocs,
    aiRecommendedDocCount,
    articleContent,
    doCreateSplitStubs,
    doProposeSplit,
    hasEditorName,
    setSplitOpen,
    setSplitProposals,
    splitCreating,
    splitOpen,
    splitProposals,
    splitWorking,
  } = props;

  if (!splitOpen) return null;

  const hasBody = articleContent.trim().length > 0;
  const recommendSplit = aiFollowOnDocs.length > 0;
  const docCount = aiRecommendedDocCount > 1 ? aiRecommendedDocCount : aiFollowOnDocs.length + 1;

  return (
    <div className="rounded-xl border border-violet-300 bg-violet-50/50 overflow-hidden shadow-sm dark:border-violet-700 dark:bg-violet-950/30">
      <div className="flex items-center justify-between gap-3 border-b border-violet-200 bg-violet-100/50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/40">
        <div>
          <div className="text-sm font-semibold text-violet-950 dark:text-violet-100">Split into sub-articles</div>
          <div className="text-[11px] text-violet-900 dark:text-violet-200">
            {recommendSplit
              ? `AI suggests ${docCount} focused articles—proposals keep real paragraphs from your body.`
              : "Optional: extract sections into separate KB articles with full text (not search chunks)."}
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-full border border-violet-400 bg-white px-3 py-1 text-xs font-medium text-violet-900 hover:bg-violet-50 dark:border-violet-500 dark:bg-zinc-900 dark:text-violet-100 dark:hover:bg-violet-950/50"
          onClick={() => setSplitOpen(false)}
        >
          Close
        </button>
      </div>

      <div className="px-4 py-3 space-y-3">
        {!hasBody ? (
          <>
            <div className="rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm text-violet-900 dark:border-violet-700 dark:bg-zinc-900/60 dark:text-violet-100">
              Add article body text in the editor above, then use <strong>Propose split</strong> to extract sections into draft articles.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="rounded-lg border border-violet-400 bg-white px-3 py-2 text-sm font-semibold text-violet-900 disabled:opacity-50 disabled:cursor-not-allowed dark:border-violet-500 dark:bg-zinc-900 dark:text-violet-100"
                disabled
              >
                Propose split
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="rounded-lg border border-violet-400 bg-white px-3 py-2 text-sm font-semibold text-violet-900 disabled:opacity-50 dark:border-violet-500 dark:bg-zinc-900 dark:text-violet-100"
                onClick={() => void doProposeSplit()}
                disabled={splitWorking || splitCreating}
              >
                {splitWorking ? "Analyzing document…" : splitProposals.length ? "Re-analyze" : "Propose split"}
              </button>
              {splitProposals.length > 0 ? <span className="text-xs text-violet-700 dark:text-violet-300">{splitProposals.length} proposed articles</span> : null}
            </div>

            {splitWorking ? (
              <div className="rounded-xl border border-violet-200 bg-white px-3 py-3 text-sm text-violet-700 dark:border-violet-700 dark:bg-zinc-900/60 dark:text-violet-200">Reading article structure and extracting sections…</div>
            ) : null}

            {!splitWorking && splitProposals.length > 0 ? (
              <div className="space-y-3">
            <div className="rounded-xl border border-violet-200 bg-white px-3 py-2 text-xs text-violet-900 dark:border-violet-700 dark:bg-zinc-900/60 dark:text-violet-100">
              Review each proposed article below. Uncheck any you don&apos;t want, edit titles or bodies as needed, then click &ldquo;Create drafts&rdquo;. The original article{" "}
              <strong>will not be modified</strong> — update or delete it separately once the split articles are complete.
            </div>

            {splitProposals.map((proposal, idx) => (
              <div
                key={idx}
                className={
                  "rounded-xl border px-3 py-3 space-y-2 " +
                  (proposal.created
                    ? "border-emerald-200 bg-emerald-50"
                    : proposal.checked
                      ? "border-violet-200 bg-white"
                      : "border-gray-200 bg-gray-50 opacity-60")
                }
              >
                <div className="flex items-center gap-2">
                  {proposal.created ? (
                    <span className="text-[11px] rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-emerald-800 font-medium">Created as draft</span>
                  ) : (
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 dark:text-zinc-300">
                      <input
                        type="checkbox"
                        checked={proposal.checked}
                        onChange={(e) =>
                          setSplitProposals((prev) => prev.map((p, i) => (i === idx ? { ...p, checked: e.target.checked } : p)))
                        }
                      />
                      Include
                    </label>
                  )}
                  <span className="text-[11px] rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-gray-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                    {proposal.doc_type || "article"} {idx + 1} of {splitProposals.length}
                  </span>
                </div>

                <div>
                  <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Title</label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
                    value={proposal.title}
                    disabled={proposal.created}
                    onChange={(e) => setSplitProposals((prev) => prev.map((p, i) => (i === idx ? { ...p, title: e.target.value } : p)))}
                  />
                </div>

                {proposal.reason ? <div className="text-[11px] text-violet-700 italic dark:text-violet-300">{proposal.reason}</div> : null}

                <div>
                  <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Body (extracted from source)</label>
                  <textarea
                    className="h-40 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
                    value={proposal.body}
                    disabled={proposal.created}
                    onChange={(e) => setSplitProposals((prev) => prev.map((p, i) => (i === idx ? { ...p, body: e.target.value } : p)))}
                  />
                </div>
              </div>
            ))}

            {splitProposals.some((p) => p.checked && !p.created) ? (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg bg-violet-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={() => void doCreateSplitStubs()}
                  disabled={splitCreating || !hasEditorName}
                >
                  {splitCreating
                    ? "Creating drafts…"
                    : `Create ${splitProposals.filter((p) => p.checked && !p.created).length} selected article${splitProposals.filter((p) => p.checked && !p.created).length === 1 ? "" : "s"} as drafts`}
                </button>
                <span className="text-xs text-violet-700 dark:text-violet-300">Library → Show drafts to find them after creating.</span>
              </div>
            ) : splitProposals.every((p) => p.created) ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100">
                All proposals created as drafts. Find them in Library → Show drafts. Remember to update or delete the original article.
              </div>
            ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
